import React, { useEffect, useState } from 'react'
import PropTypes from 'prop-types'
import { SplitLoginEmailPage } from '../SplitLoginEmailPage'
import { SplitLoginPasswordPage } from '../SplitLoginPasswordPage'
import { SplitLoginEmailPinCodeCheckPage } from '../SplitLoginEmailPinCodeCheckPage'
import {
  loginWithFacialRecognition,
  loginWithTurnstilev2,
  loginWithValidation,
} from '../../../api/loginApiCall'
import { LoadingBackdrop } from '@olxbr/design-system'
import passwordlessSendEmailApiCall from '../../../api/passwordlessSendEmailApiCall'
import { LoginMethodChooserPage } from '../LoginMethodChooserPage'
import { LoginHeader } from './LoginHeader'
import { isAccountRevalidationExperimentOn, isLoginScrollEnabled } from '../../../util/experiments'
import getSessionTokenInfos from '../../../util/sessionTokenGetter'
import { HelpModalTemplate } from '../../templates/HelpModalTemplate'
import { PasswordHelpModalTemplate } from '../../templates/HelpModalTemplate/PasswordHelpModalTemplate'
import { PhoneValidation as PhoneValidationStack } from '../PhoneValidationStack/PhoneValidation'
import { Phone2FALoginChallenge } from '../../pages/Phone2FALoginChallenge'
import { ScorePinCodeStack } from '../../pages/ScorePinCodeStack'
import Lurker from '../../../util/lurker'
import { userChallengesFacialRecognition } from '../../../config/staticUrl'
import storage from '../../../util/storage'
import { LOGIN_METHOD_KEY } from '../../../util/global.consts'
import { isValidMustTypify } from '../../../util/account'
import { EMAIL_LOGIN_THEMES } from '../../templates/EmailLoginTemplate/EmailLoginThemes'

const lurker = new Lurker('login')

const PAGES = {
  EMAIL_PAGE: 'EmailPage',
  METHOD_CHOOSER_PAGE: 'MethodChooserPage',
  PASSWORD_PAGE: 'PasswordPage',
  EMAIL_PINCODE_PAGE: 'InsertEmailPincodePage',
  NEED_HELP: 'NeedHelpOptions',
  VALIDATE_PHONE: 'PhoneValidation',
  PHONE_2FA: 'phone2fa',
  EMAIL_2FA: 'email2fa',
}

const defaultAllowedMethods = { EMAIL: true, PASSWORD: true, FACIAL_RECOGNITION: false }

const getInitialPage = () => PAGES.EMAIL_PAGE

const SplitLoginPage = ({
  facebookAppId,
  onChallengePinCodeCall,
  onError,
  onForgotPassword,
  onLoginSuccess,
  onMagicLinkClick,
  onMagicLinkEmailSentClick,
  onReactivationLinkClick,
  onRegisterClick,
  onPhoneNotValidated,
  onRequireEmail,
  onAccountNotValidated,
  onAccountMustValidate,
  passwordlessSignIn,
  showReactivationLink,
  showHelpingModalLink,
  smartLockLogin,
  modalMode,
  onCloseModal,
  abTests,
  redirectToVerifyYourAccess,
  onAccountSocialLoginTyping,
  onAccountCompleteData,
  themeName,
  turnstileEnabled = true,
}) => {
  const [email, setEmail] = React.useState('')
  const [allowedMethods, setAllowedMethods] = React.useState(defaultAllowedMethods)
  const [isLoading, setIsLoading] = React.useState(false)
  const [currentPage, setCurrentPage] = React.useState(getInitialPage())
  const [phone2faData, setPhone2faData] = React.useState(null)
  const [phone2faTokenData, setPhone2faTokenData] = React.useState(null)
  const [email2faData, setEmail2faData] = React.useState(null)
  const [email2faTokenData, setEmail2faTokenData] = React.useState(null)

  const [sessionTokens, setSessionTokens] = React.useState(null)
  const [phoneValidationToken, setPhoneValidationToken] = React.useState(null)
  const [showHelpModal, setShowHelpModal] = useState(false)

  const getSessionResult = async () => {
    const sessionTokensResult = await getSessionTokenInfos()
    if (sessionTokensResult) {
      setSessionTokens(sessionTokensResult)
    }
  }

  useEffect(() => {
    getSessionResult()
  }, [])

  const onEmailPageSubmit = (email, currentAllowedMethods) => {
    setEmail(email)
    setCurrentPage(PAGES.METHOD_CHOOSER_PAGE)
    setAllowedMethods(prevAllowedMethods => ({ ...prevAllowedMethods, ...currentAllowedMethods }))
  }

  const onPasswordSubmit = async (password, turnstileToken, sessionTokens) => {
    const loginApiCaller = isAccountRevalidationExperimentOn(abTests)
      ? loginWithValidation(email, password, sessionTokens)
      : loginWithTurnstilev2(email, password, turnstileToken, sessionTokens, abTests)

    const isMustTypify = await isValidMustTypify()

    return loginApiCaller.then(async data => {
      const { isTypified } = data

      if (!isTypified && isMustTypify) {
        return onAccountCompleteData()
      }

      onLoginSuccess(data)
      return data
    })
  }

  const onMaxPasswordAttemptsReached = () => {
    return passwordlessSendEmailApiCall(email)
  }

  const getBackToEmailPage = () => {
    setIsLoading(true)
    setCurrentPage(PAGES.EMAIL_PAGE)
    setIsLoading(false)
  }

  const getBackToMethodChoosePage = () => {
    setIsLoading(true)
    setCurrentPage(PAGES.METHOD_CHOOSER_PAGE)
    setIsLoading(false)
  }

  const goToPasswordPage = () => {
    setIsLoading(true)
    setCurrentPage(PAGES.PASSWORD_PAGE)
    setIsLoading(false)
  }

  const goToFacialRecognitionPage = () => {
    setIsLoading(true)

    lurker.clickEvt('facial_recognition_button_click')
    loginWithFacialRecognition(email).then(() => {
      window.location.assign(userChallengesFacialRecognition)
    })
  }

  const goToEmailPincodePage = () => {
    setIsLoading(true)
    setCurrentPage(PAGES.EMAIL_PINCODE_PAGE)
    setIsLoading(false)
  }

  const goToPhone2fa = () => {
    setIsLoading(true)
    setCurrentPage(PAGES.PHONE_2FA)
    setIsLoading(false)
  }

  const goToEmail2fa = () => {
    setIsLoading(true)
    setCurrentPage(PAGES.EMAIL_2FA)
    setIsLoading(false)
  }

  const goToNeedHelp = () => {
    setIsLoading(true)
    setCurrentPage(PAGES.NEED_HELP)
    setIsLoading(false)
  }

  const handlePhone2faFlow = (data, method) => {
    globalThis.loginMethod = method
    if (!modalMode) {
      onChallengePinCodeCall(data)
    } else {
      redirectTo2fa(data)
    }
  }

  const redirectTo2fa = challengeInfo => {
    const { token, send } = challengeInfo

    const redirectOptions = {
      PHONE: setPhone2faState,
      EMAIL: setEmail2faState,
    }

    redirectOptions[send.type](send.to, token)
  }

  const setPhone2faState = (phone, token) => {
    setPhone2faData(phone)
    setPhone2faTokenData(token)
  }

  const setEmail2faState = (phone, token) => {
    setEmail2faData(phone)
    setEmail2faTokenData(token)
  }

  useEffect(() => {
    if (phone2faData !== null && phone2faTokenData !== null) {
      goToPhone2fa()
    }
  }, [phone2faData, phone2faTokenData])

  useEffect(() => {
    if (email2faData !== null && email2faTokenData !== null) {
      goToEmail2fa()
    }
  }, [email2faData, email2faTokenData])

  const goToPhoneValidation = () => {
    setIsLoading(true)
    setCurrentPage(PAGES.VALIDATE_PHONE)
    setIsLoading(false)
  }

  const handlePhoneNotValidatedFlow = (token, method) => {
    globalThis.loginMethod = method
    if (modalMode) {
      setPhoneValidationToken(token)
    } else {
      onPhoneNotValidated(token)
    }
  }

  useEffect(() => {
    if (modalMode && phoneValidationToken) {
      goToPhoneValidation()
    }
  }, [phoneValidationToken])

  const renderCurrentPage = () => {
    const pages = {
      [PAGES.EMAIL_PAGE]: (
        <SplitLoginEmailPage
          facebookAppId={facebookAppId}
          onChallengePinCodeCall={data => handlePhone2faFlow(data, PAGES.EMAIL_PAGE)}
          onError={onError}
          onForgotPassword={onForgotPassword}
          onLoginSuccess={onLoginSuccess}
          onMagicLinkClick={onMagicLinkClick}
          onMagicLinkEmailSentClick={onMagicLinkEmailSentClick}
          onReactivationLinkClick={onReactivationLinkClick}
          onRegisterClick={onRegisterClick}
          onRequireEmail={onRequireEmail}
          onSubmit={onEmailPageSubmit}
          showReactivationLink={showReactivationLink}
          showHelpingModalLink={showHelpingModalLink}
          smartLockLogin={smartLockLogin}
          onPhoneNotValidatedError={data => handlePhoneNotValidatedFlow(data, PAGES.EMAIL_PAGE)}
          abTests={abTests}
          sessionTokens={sessionTokens}
          onAccountNotValidated={onAccountNotValidated}
          onAccountMustValidate={onAccountMustValidate}
          modalMode={modalMode}
          onClickNeedHelp={goToNeedHelp}
          onAccountSocialLoginTyping={onAccountSocialLoginTyping}
          onAccountCompleteData={onAccountCompleteData}
          themeName={themeName}
        />
      ),
      [PAGES.METHOD_CHOOSER_PAGE]: (
        <LoginMethodChooserPage
          email={email}
          allowedMethods={allowedMethods}
          onBackClick={getBackToEmailPage}
          onForgotPasswordClick={onForgotPassword}
          onMagicLinkClick={onMagicLinkClick}
          onReactivationLinkClick={onReactivationLinkClick}
          onPasswordMethodClick={goToPasswordPage}
          onEmailMethodClick={goToEmailPincodePage}
          onFacialRecognitionMethodClick={goToFacialRecognitionPage}
          showHelpingModalLink={showHelpingModalLink}
          modalMode={modalMode}
          abTests={abTests}
        />
      ),
      [PAGES.PASSWORD_PAGE]: (
        <SplitLoginPasswordPage
          email={email}
          passwordlessSignIn={passwordlessSignIn}
          onBackClick={getBackToMethodChoosePage}
          onChallengePinCodeCall={data => handlePhone2faFlow(data, PAGES.PASSWORD_PAGE)}
          onError={onError}
          onForgotPassword={onForgotPassword}
          onLoginSuccess={onLoginSuccess}
          onSubmit={onPasswordSubmit}
          onMaxPasswordAttemptsReached={onMaxPasswordAttemptsReached}
          onPhoneNotValidatedError={data => handlePhoneNotValidatedFlow(data, PAGES.PASSWORD_PAGE)}
          onAccountNotValidated={onAccountNotValidated}
          onAccountMustValidate={onAccountMustValidate}
          abTests={abTests}
          sessionTokens={sessionTokens}
          modalMode={modalMode}
          onAccountCompleteData={onAccountCompleteData}
          turnstileEnabled={turnstileEnabled}
        />
      ),
      [PAGES.EMAIL_PINCODE_PAGE]: (
        <SplitLoginEmailPinCodeCheckPage
          email={email}
          abTests={abTests}
          onBackClick={getBackToMethodChoosePage}
          onSuccess={onLoginSuccess}
          onChallengePinCodeCall={data => handlePhone2faFlow(data, PAGES.EMAIL_PINCODE_PAGE)}
          onPhoneNotValidatedError={data =>
            handlePhoneNotValidatedFlow(data, PAGES.EMAIL_PINCODE_PAGE)
          }
          sessionTokens={sessionTokens}
          onAccountNotValidated={onAccountNotValidated}
          onAccountMustValidate={onAccountMustValidate}
          modalMode={modalMode}
          onAccountCompleteData={onAccountCompleteData}
        />
      ),
    }

    return pages[currentPage] || pages[PAGES.EMAIL_PAGE]
  }

  useEffect(() => {
    storage.remove(LOGIN_METHOD_KEY)
    return () => {
      storage.remove(LOGIN_METHOD_KEY)
    }
  }, [])

  const PAGE_LURKER_MAP = {
    [PAGES.EMAIL_PAGE]: 'login_email',
    [PAGES.METHOD_CHOOSER_PAGE]: 'login_method_chooser',
    [PAGES.PASSWORD_PAGE]: 'login_password',
    [PAGES.EMAIL_PINCODE_PAGE]: 'login_email',
  }

  const experimentOn = isLoginScrollEnabled(abTests)

  const currentPageWithHeader = () => {
    const handleHeaderHelpClick = () => {
      const pageLurkerName = PAGE_LURKER_MAP[currentPage] || 'login'
      const pageLurker = new Lurker(pageLurkerName)
      pageLurker.clickEvt('help_modal_open')
      setShowHelpModal(true)
    }

    const showGoBack = experimentOn && currentPage !== PAGES.EMAIL_PAGE
    const goBackHandler = () => {
      if (currentPage === PAGES.METHOD_CHOOSER_PAGE) {
        getBackToEmailPage()
      } else {
        getBackToMethodChoosePage()
      }
    }

    return (
      <>
        {!modalMode && (
          <LoginHeader
            onHelpClick={experimentOn ? handleHeaderHelpClick : undefined}
            showGoBack={showGoBack}
            onGoBack={goBackHandler}
          />
        )}
        {renderCurrentPage()}
        {experimentOn && currentPage === PAGES.PASSWORD_PAGE && (
          <PasswordHelpModalTemplate
            open={showHelpModal}
            onClose={() => {
              const pageLurkerName = PAGE_LURKER_MAP[currentPage] || 'login'
              const pageLurker = new Lurker(pageLurkerName)
              pageLurker.clickEvt('help_modal_close')
              setShowHelpModal(false)
            }}
          />
        )}
        {experimentOn && currentPage !== PAGES.PASSWORD_PAGE && (
          <HelpModalTemplate
            abTests={abTests}
            open={showHelpModal}
            onForgotPasswordLinkClick={onForgotPassword}
            onReactivationLinkClick={onReactivationLinkClick}
            onMagicLinkClick={onMagicLinkClick}
            onClose={() => {
              const pageLurkerName = PAGE_LURKER_MAP[currentPage] || 'login'
              const pageLurker = new Lurker(pageLurkerName)
              pageLurker.clickEvt('help_modal_close')
              setShowHelpModal(false)
            }}
          />
        )}
      </>
    )
  }

  return isLoading ? <LoadingBackdrop /> : currentPageWithHeader()
}

SplitLoginPage.propTypes = {
  abTests: PropTypes.string,
  facebookAppId: PropTypes.string.isRequired,
  onChallengePinCodeCall: PropTypes.func,
  onError: PropTypes.func,
  onForgotPassword: PropTypes.func,
  onLoginSuccess: PropTypes.func,
  onMagicLinkClick: PropTypes.func,
  onMagicLinkEmailSentClick: PropTypes.func,
  onReactivationLinkClick: PropTypes.func,
  onRegisterClick: PropTypes.func,
  onRequireEmail: PropTypes.func,
  onPhoneNotValidated: PropTypes.func,
  onAccountNotValidated: PropTypes.func,
  onAccountMustValidate: PropTypes.func,
  passwordlessSignIn: PropTypes.bool,
  showReactivationLink: PropTypes.bool,
  showHelpingModalLink: PropTypes.bool,
  smartLockLogin: PropTypes.bool,
  modalMode: PropTypes.bool,
  onCloseModal: PropTypes.func,
  redirectToVerifyYourAccess: PropTypes.func,
  onAccountSocialLoginTyping: PropTypes.func,
  onAccountCompleteData: PropTypes.func,
  themeName: PropTypes.oneOf(Object.keys(EMAIL_LOGIN_THEMES)).isRequired,
  turnstileEnabled: PropTypes.bool,
}

export { SplitLoginPage }
